import { useEffect } from 'react';
import { useEdgesState, useNodesState } from '@xyflow/react';
import type { GraphViewState } from '../../store/ui';
import type { Graph } from '../../types/graph';
import { toRFEdges, toRFNodes } from './utils';
import type { RFGraphEdge, RFGraphNode } from './utils';

const samePosition = (left: RFGraphNode, right: RFGraphNode) => {
  return (
    left.position.x === right.position.x && left.position.y === right.position.y
  );
};

const sameNode = (left: RFGraphNode, right: RFGraphNode) => {
  return (
    left.id === right.id &&
    left.type === right.type &&
    left.draggable === right.draggable &&
    left.selectable === right.selectable &&
    left.data.nodeId === right.data.nodeId &&
    left.data.kind === right.data.kind &&
    left.data.treeIndex === right.data.treeIndex &&
    samePosition(left, right)
  );
};

const sameEdge = (left: RFGraphEdge, right: RFGraphEdge) => {
  return (
    left.id === right.id &&
    left.source === right.source &&
    left.target === right.target &&
    left.sourceHandle === right.sourceHandle &&
    left.targetHandle === right.targetHandle &&
    left.type === right.type &&
    left.data?.edgeId === right.data?.edgeId &&
    left.data?.order === right.data?.order
  );
};

export const useGraphViewState = (graph: Graph, view: GraphViewState) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(
    toRFNodes(graph, view)
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(toRFEdges(graph));

  useEffect(() => {
    setNodes((prevNodes) => {
      const nextNodes = toRFNodes(graph, view);
      const previousById = new Map(prevNodes.map((node) => [node.id, node]));
      let didChange = prevNodes.length !== nextNodes.length;
      const mergedNodes = nextNodes.map((node) => {
        const previous = previousById.get(node.id);
        if (!previous) {
          didChange = true;
          return node;
        }

        if (sameNode(previous, node)) {
          return previous;
        }

        didChange = true;
        return {
          ...previous,
          ...node,
          data: node.data,
          position: node.position,
          type: node.type,
        };
      });

      if (
        !didChange &&
        mergedNodes.every((node, index) => node === prevNodes[index])
      ) {
        return prevNodes;
      }

      return mergedNodes;
    });
  }, [graph, view, setNodes]);

  useEffect(() => {
    setEdges((prevEdges) => {
      const nextEdges = toRFEdges(graph);
      if (
        prevEdges.length === nextEdges.length &&
        prevEdges.every((edge, index) => sameEdge(edge, nextEdges[index]))
      ) {
        return prevEdges;
      }
      return nextEdges;
    });
  }, [graph, setEdges]);

  return {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
  };
};
